import { useState, useEffect, useMemo } from "react";
import { COUNTRIES, SERVICE_PLAN_LABELS } from "@/pages/admin/system/constants";

interface NotificationPanelProps {
  fetchClients: (params?: Record<string, unknown>) => Promise<any>;
  sendBulkNotification: (payload: { client_ids: number[]; title: string; content: string; type?: string; scheduled_at?: string | null }) => Promise<any>;
  onScheduledSent: () => void;
  selectedClients: number[];
  setSelectedClients: (ids: number[] | ((prev: number[]) => number[])) => void;
  onMessage: (msg: string) => void;
}

export default function NotificationPanel({
  fetchClients,
  sendBulkNotification,
  onScheduledSent,
  selectedClients,
  setSelectedClients,
  onMessage,
}: NotificationPanelProps) {
  const [clients, setClients] = useState<any[]>([]);
  const [clientSearch, setClientSearch] = useState("");
  const [selectAll, setSelectAll] = useState(false);
  const [filterCountry, setFilterCountry] = useState("all");
  const [filterServicePlan, setFilterServicePlan] = useState("all");
  const [filterProgressStatus, setFilterProgressStatus] = useState("all");
  const [notifyTitle, setNotifyTitle] = useState("");
  const [notifyContent, setNotifyContent] = useState("");
  const [notifyType, setNotifyType] = useState("info");
  const [useSchedule, setUseSchedule] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const load = async () => {
      const res = await fetchClients({ limit: 500 });
      if (res?.success) setClients(res.clients || []);
    };
    load();
  }, [fetchClients]);

  const filteredClients = useMemo(() => {
    return clients.filter((c) => {
      const q = clientSearch.toLowerCase();
      const matchSearch = !q || (c.name || "").toLowerCase().includes(q) || (c.phone || "").toLowerCase().includes(q) || ((c as any).client_code || "").toLowerCase().includes(q);
      const matchCountry = filterCountry === "all" || c.country === filterCountry;
      const matchPlan = filterServicePlan === "all" || c.service_plan === filterServicePlan;
      const matchProgress = filterProgressStatus === "all"
        || (filterProgressStatus === "active" && c.current_stage_status === "in_progress")
        || (filterProgressStatus === "completed" && c.current_stage_status === "completed");
      return matchSearch && matchCountry && matchPlan && matchProgress;
    });
  }, [clients, clientSearch, filterCountry, filterServicePlan, filterProgressStatus]);

  const handleToggleClient = (id: number) => {
    setSelectedClients((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
    setSelectAll(false);
  };

  const handleSelectAll = () => {
    if (selectAll) { setSelectedClients([]); setSelectAll(false); }
    else { setSelectedClients(filteredClients.map((c) => c.id)); setSelectAll(true); }
  };

  const handleSend = async () => {
    if (selectedClients.length === 0 || !notifyTitle.trim() || !notifyContent.trim()) {
      onMessage("请选择客户并填写完整通知信息"); return;
    }
    if (useSchedule && !scheduledAt) { onMessage("请选择定时发送时间"); return; }
    if (useSchedule && scheduledAt && new Date(scheduledAt) <= new Date()) { onMessage("定时发送时间必须晚于当前操作时间"); return; }
    setSending(true);
    let scheduledIso: string | null = null;
    if (useSchedule && scheduledAt) {
      const [datePart, timePart] = scheduledAt.split('T');
      const [y, m, d] = datePart.split('-').map(Number);
      const [hh, mm] = timePart.split(':').map(Number);
      scheduledIso = new Date(y, m - 1, d, hh, mm).toISOString();
    }
    const res = await sendBulkNotification({
      client_ids: selectedClients,
      title: notifyTitle.trim(),
      content: notifyContent.trim(),
      type: notifyType,
      scheduled_at: scheduledIso,
    });
    setSending(false);
    if (res?.success) {
      const msg = useSchedule
        ? `已为 ${res.sent_count} 位客户创建定时通知，将于指定时间发送！`
        : `成功向 ${res.sent_count} 位客户推送通知！`;
      onMessage(msg);
      setNotifyTitle(""); setNotifyContent(""); setSelectedClients([]); setSelectAll(false); setUseSchedule(false); setScheduledAt("");
      if (useSchedule) onScheduledSent();
    } else {
      onMessage(res?.error || "推送失败");
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="bg-white rounded-xl shadow-sm p-5 flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-[#1a3a5c]">选择接收客户</h3>
              <p className="text-[11px] text-gray-400 mt-0.5">已选 {selectedClients.length} / {filteredClients.length} 位客户</p>
            </div>
            <div className="flex items-center gap-2">
              {selectedClients.length > 0 && (
                <button onClick={() => { setSelectedClients([]); setSelectAll(false); }} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-red-50 text-red-500 hover:bg-red-100 transition-colors cursor-pointer whitespace-nowrap">
                  清除选中
                </button>
              )}
              <button onClick={handleSelectAll}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${selectAll ? "bg-[#1a3a5c] text-white" : "bg-gray-50 text-gray-600 hover:bg-gray-100"}`}>
                {selectAll ? "取消全选" : "全部选择"}
              </button>
            </div>
          </div>
          <div className="relative">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
            <input type="text" value={clientSearch} onChange={(e) => setClientSearch(e.target.value)}
              placeholder="搜索客户姓名或客户编码"
              className="w-full pl-8 pr-3 py-2 rounded-lg border border-gray-200 text-xs text-[#1a3a5c] placeholder:text-gray-300 focus:outline-none focus:border-[#c9a96e] bg-white" />
          </div>
          <div className="flex gap-2 flex-wrap">
            <select value={filterCountry} onChange={(e) => setFilterCountry(e.target.value)}
              className="flex-1 min-w-0 px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white cursor-pointer">
              <option value="all">全部国家</option>
              {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={filterServicePlan} onChange={(e) => setFilterServicePlan(e.target.value)}
              className="flex-1 min-w-0 px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white cursor-pointer">
              <option value="all">全部方案</option>
              {Object.entries(SERVICE_PLAN_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select value={filterProgressStatus} onChange={(e) => setFilterProgressStatus(e.target.value)}
              className="flex-1 min-w-0 px-2.5 py-1.5 rounded-lg border border-gray-200 text-xs text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white cursor-pointer">
              <option value="all">全部进度</option>
              <option value="active">在途客户</option>
              <option value="completed">已结束</option>
            </select>
          </div>
          <div className="flex-1 overflow-y-auto max-h-64 space-y-1 border border-gray-100 rounded-lg">
            {filteredClients.length === 0 ? (
              <div className="p-4 text-center text-xs text-gray-400">暂无客户</div>
            ) : (
              filteredClients.map((c) => (
                <label key={c.id} className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors hover:bg-gray-50 ${selectedClients.includes(c.id) ? "bg-[#1a3a5c]/5" : ""}`}>
                  <div className={`w-4 h-4 rounded flex items-center justify-center shrink-0 transition-colors ${selectedClients.includes(c.id) ? "bg-[#1a3a5c]" : "border border-gray-300"}`}>
                    {selectedClients.includes(c.id) && <i className="ri-check-line text-white text-[10px]"></i>}
                  </div>
                  <input type="checkbox" checked={selectedClients.includes(c.id)} onChange={() => handleToggleClient(c.id)} className="hidden" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-[#1a3a5c] truncate">
                      {c.is_single === false && c.partner_name ? `${c.name || "未命名"} & ${c.partner_name}` : (c.name || "未命名")}
                    </p>
                    <p className="text-[10px] text-gray-400">{((c as any).client_code || c.phone)}{c.country ? ` · ${c.country}` : ""}</p>
                  </div>
                </label>
              ))
            )}
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm p-5 space-y-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#1a3a5c] flex items-center justify-center">
              <i className="ri-send-plane-line text-white text-sm"></i>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[#1a3a5c]">编写通知内容</h3>
              <p className="text-[11px] text-gray-400">支持批量发送和定时发送</p>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-2">通知类型</label>
            <div className="flex gap-2 flex-wrap">
              {[
                { value: "info", label: "系统", icon: "ri-information-line", color: "bg-[#1a3a5c]" },
                { value: "success", label: "好消息", icon: "ri-checkbox-circle-line", color: "bg-emerald-500" },
                { value: "warning", label: "提醒", icon: "ri-alert-line", color: "bg-amber-500" },
                { value: "error", label: "紧急", icon: "ri-close-circle-line", color: "bg-red-500" },
              ].map((t) => (
                <button key={t.value} onClick={() => setNotifyType(t.value)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${notifyType === t.value ? `${t.color} text-white` : "bg-gray-50 text-gray-500 hover:bg-gray-100"}`}>
                  <i className={t.icon}></i>{t.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">通知标题</label>
            <input type="text" value={notifyTitle} onChange={(e) => setNotifyTitle(e.target.value)} placeholder="例如：胚胎培养报告已出"
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-[#1a3a5c] placeholder:text-gray-300 focus:outline-none focus:border-[#c9a96e] focus:ring-1 focus:ring-[#c9a96e]/20 transition-all bg-white" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1.5">通知内容</label>
            <textarea value={notifyContent} onChange={(e) => setNotifyContent(e.target.value)} rows={3} maxLength={500} placeholder="填写通知详情内容..."
              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-[#1a3a5c] placeholder:text-gray-300 focus:outline-none focus:border-[#c9a96e] focus:ring-1 focus:ring-[#c9a96e]/20 transition-all bg-white resize-none" />
            <p className="text-[10px] text-gray-300 mt-1 text-right">{notifyContent.length}/500</p>
          </div>
          <div className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2.5">
            <div>
              <p className="text-xs font-medium text-gray-700">定时发送</p>
              <p className="text-[10px] text-gray-400 mt-0.5">选择发送时间，到时自动推送</p>
            </div>
            <button onClick={() => setUseSchedule((v) => !v)}
              className={`w-10 h-5 rounded-full transition-colors cursor-pointer relative ${useSchedule ? "bg-[#1a3a5c]" : "bg-gray-300"}`}>
              <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${useSchedule ? "left-5.5" : "left-0.5"}`}></span>
            </button>
          </div>
          {useSchedule && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1.5">发送时间</label>
              <input type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} min={new Date().toISOString().slice(0, 16)}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-[#1a3a5c] focus:outline-none focus:border-[#c9a96e] bg-white cursor-pointer" />
            </div>
          )}
          <div className="flex items-center justify-between pt-2 border-t border-gray-100">
            <p className="text-xs text-gray-400">{selectedClients.length > 0 ? `将发送给 ${selectedClients.length} 位客户` : "请先选择客户"}</p>
            <button onClick={handleSend} disabled={sending || selectedClients.length === 0 || !notifyTitle.trim() || !notifyContent.trim()}
              className="px-5 py-2.5 bg-[#1a3a5c] text-white rounded-lg text-sm font-medium hover:bg-[#142d48] transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap">
              {sending ? <><i className="ri-loader-4-line animate-spin mr-1"></i>发送中...</>
                : useSchedule ? <><i className="ri-time-line mr-1"></i>定时推送</>
                : <><i className="ri-send-plane-line mr-1"></i>立即推送</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}